import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { fetchGenerationAccess } from '@/lib/generationAccess';
import type { ServerGenerationAccess } from '@/lib/generationAccess';

export type UsageAction = 'video_generate' | 'image_upload' | 'text_explanation' | 'ai_chat';

export interface DailyUsage {
  videosGenerated: number;
  imagesUploaded: number;
  textExplanations: number;
  aiChatMessages: number;
  date: string;
}

type UsageCounterField = Exclude<keyof DailyUsage, 'date'>;

export interface TierConfig {
  tier: 'free' | 'premium';
  videoAllowance: number;
  dailyUploadLimit: number;
  videoMaxDuration: number;
  videoMinDuration: number;
  hdEnabled: boolean;
  downloadsEnabled: boolean;
  priorityProcessing: boolean;
  storageDurationHours: number;
}

interface UsageContextValue {
  usage: DailyUsage;
  tier: TierConfig;
  checkUsage: (action: UsageAction) => { allowed: boolean; remaining: number; limit: number; unlimited?: boolean };
  recordUsage: (action: UsageAction) => void;
  resetDailyUsage: () => void;
  refreshUsage: () => void;
  isLoading: boolean;
  generationAccess: ServerGenerationAccess | null;
  accessError: string;
}

const USAGE_KEY = 'lavyn_daily_usage_v1';

const FREE_TIER: TierConfig = {
  tier: 'free',
  videoAllowance: 4,
  dailyUploadLimit: 5,
  videoMaxDuration: 180,
  videoMinDuration: 0,
  hdEnabled: false,
  downloadsEnabled: false,
  priorityProcessing: false,
  storageDurationHours: 24,
};

const PREMIUM_TIER: TierConfig = {
  tier: 'premium',
  videoAllowance: 12,
  dailyUploadLimit: 50,
  videoMaxDuration: 600,
  videoMinDuration: 0,
  hdEnabled: true,
  downloadsEnabled: true,
  priorityProcessing: true,
  storageDurationHours: 720,
};


function getToday(): string {
  return new Date().toISOString().split('T')[0];
}

function loadUsage(): DailyUsage {
  try {
    const raw = localStorage.getItem(USAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as DailyUsage;
      if (parsed.date === getToday()) return parsed;
    }
  } catch { /* ignore */ }
  return {
    videosGenerated: 0,
    imagesUploaded: 0,
    textExplanations: 0,
    aiChatMessages: 0,
    date: getToday(),
  };
}

function saveUsage(u: DailyUsage) {
  try {
    localStorage.setItem(USAGE_KEY, JSON.stringify(u));
  } catch { /* ignore */ }
}


const actionFieldMap: Record<UsageAction, UsageCounterField> = {
  video_generate: 'videosGenerated',
  image_upload: 'imagesUploaded',
  text_explanation: 'textExplanations',
  ai_chat: 'aiChatMessages',
};

const UsageContext = createContext<UsageContextValue | null>(null);

export function UsageProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const [usage, setUsage] = useState<DailyUsage>(loadUsage);
  const [generationAccess, setGenerationAccess] = useState<ServerGenerationAccess | null>(null);
  const [accessError, setAccessError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const tier: TierConfig = generationAccess?.tier === 'PREMIUM'
    ? { ...PREMIUM_TIER, videoAllowance: generationAccess.periodLimit }
    : { ...FREE_TIER, videoAllowance: generationAccess?.periodLimit || FREE_TIER.videoAllowance };

  useEffect(() => {
    if (authLoading) return;
    let active = true;
    setGenerationAccess(null);
    setAccessError('');
    setIsLoading(true);
    const refresh = async () => {
      try {
        const access = await fetchGenerationAccess();
        if (active) { setGenerationAccess(access); setAccessError(''); }
      } catch (error) {
        if (active) { setGenerationAccess(null); setAccessError(error instanceof Error ? error.message : 'Generation access unavailable.'); }
      } finally {
        if (active) setIsLoading(false);
      }
    };
    void refresh();
    const onRefresh = () => { void refresh(); };
    window.addEventListener('lavyn:generation-usage-changed', onRefresh);
    window.addEventListener('focus', onRefresh);
    const timer = window.setInterval(onRefresh, 60000);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('lavyn:generation-usage-changed', onRefresh);
      window.removeEventListener('focus', onRefresh);
    };
  }, [user?.id, authLoading]);

  // Ensure daily reset
  useEffect(() => {
    const today = getToday();
    if (usage.date !== today) {
      const fresh = {
        videosGenerated: 0,
        imagesUploaded: 0,
        textExplanations: 0,
        aiChatMessages: 0,
        date: today,
      };
      setUsage(fresh);
      saveUsage(fresh);
    }
  }, [usage.date]);

  // Persist to localStorage
  useEffect(() => {
    saveUsage(usage);
  }, [usage]);

  const checkUsage = useCallback((action: UsageAction) => {
    if (action === 'video_generate') {
      if (!generationAccess) return { allowed: false, remaining: 0, limit: FREE_TIER.videoAllowance };
      return {
        allowed: generationAccess.allowed === true && (generationAccess.unlimited === true || generationAccess.remainingVideos > 0),
        remaining: generationAccess.remainingVideos,
        limit: generationAccess.periodLimit,
        unlimited: generationAccess.unlimited === true,
      };
    }
    const field = actionFieldMap[action];
    const current = usage[field] || 0;
    // Uploads retain their independent daily limit; text/chat never use video quota.
    const limit = action === 'image_upload' ? tier.dailyUploadLimit : 9999;
    return {
      allowed: current < limit,
      remaining: Math.max(0, limit - current),
      limit,
    };
  }, [usage, tier, generationAccess]);

  const recordUsage = useCallback((action: UsageAction) => {
    if (action === 'video_generate') {
      window.dispatchEvent(new Event('lavyn:generation-usage-changed'));
      return;
    }
    const field = actionFieldMap[action];
    setUsage((prev) => {
      const next = { ...prev, [field]: (prev[field] || 0) + 1 };
      saveUsage(next);
      return next;
    });

  }, []);

  const resetDailyUsage = useCallback(() => {
    const fresh = {
      videosGenerated: 0,
      imagesUploaded: 0,
      textExplanations: 0,
      aiChatMessages: 0,
      date: getToday(),
    };
    setUsage(fresh);
    saveUsage(fresh);
  }, []);

  const refreshUsage = useCallback(() => {
    setUsage(loadUsage());
    window.dispatchEvent(new Event('lavyn:generation-usage-changed'));
  }, []);

  return (
    <UsageContext.Provider
      value={{
        usage: { ...usage, videosGenerated: generationAccess?.periodUsed || 0 },
        tier,
        checkUsage,
        recordUsage,
        resetDailyUsage,
        refreshUsage,
        isLoading,
        generationAccess,
        accessError,
      }}
    >
      {children}
    </UsageContext.Provider>
  );
}

export function useUsage() {
  const ctx = useContext(UsageContext);
  if (!ctx) throw new Error('useUsage must be used within UsageProvider');
  return ctx;
}